import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { jobsApi } from '@/lib/api';
import { 
  Truck, 
  ArrowLeft, 
  MapPin, 
  Calendar,
  Package,
  FileText,
  Loader2,
  CheckCircle
} from 'lucide-react';

const loadTypes = [
  'General Freight',
  'Equipment',
  'Machinery',
  'Auto Parts',
  'Construction Materials',
  'Oil Field Equipment',
  'Agricultural',
  'Livestock',
  'Other'
];

const states = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NT', name: 'Northern Territory' }
];

export default function PostJobPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pickup_location: '',
    pickup_city: '',
    pickup_state: '',
    pickup_postcode: '',
    delivery_location: '',
    delivery_city: '',
    delivery_state: '',
    delivery_postcode: '',
    pickup_date: '',
    delivery_deadline: '',
    load_type: '',
    weight_kg: '',
    dimensions: '',
    special_requirements: '',
    authority_to_leave: false,
    atl_instructions: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const required = ['title', 'description', 'pickup_location', 'pickup_city', 'pickup_state',
      'delivery_location', 'delivery_city', 'delivery_state', 'pickup_date', 'delivery_deadline',
      'load_type', 'weight_kg'];
    
    const missing = required.filter(field => !formData[field]);
    if (missing.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const jobData = {
        ...formData,
        weight_kg: parseFloat(formData.weight_kg)
      };
      
      await jobsApi.create(jobData);
      toast.success('Job posted successfully! Providers have been notified.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container-app py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-sm" style={{ fontFamily: 'Barlow Condensed' }}>
              THM
            </span>
          </Link>
        </div>
      </header>

      <main className="container-app py-8 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              POST A HOTSHOT JOB
            </h1>
            <p className="text-muted-foreground">
              Fill in the details below. Matching providers will be notified instantly.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Job Details */}
            <div className="form-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <h2 className="form-section-title mb-0">Job Details</h2>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Urgent equipment delivery to Dallas"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    data-testid="job-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your load and any important details..."
                    rows={4}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    data-testid="job-description-input"
                  />
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="load_type">Load Type *</Label>
                    <Select value={formData.load_type} onValueChange={(v) => handleChange('load_type', v)}>
                      <SelectTrigger data-testid="load-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {loadTypes.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="weight_kg">Weight (kg) *</Label>
                    <Input
                      id="weight_kg"
                      type="number"
                      placeholder="e.g., 1000"
                      value={formData.weight_kg}
                      onChange={(e) => handleChange('weight_kg', e.target.value)}
                      data-testid="weight-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="dimensions">Dimensions (Optional)</Label>
                  <Input
                    id="dimensions"
                    placeholder="e.g., 2.4m x 1.2m x 0.9m"
                    value={formData.dimensions}
                    onChange={(e) => handleChange('dimensions', e.target.value)}
                    data-testid="dimensions-input"
                  />
                </div>
              </div>
            </div>

            {/* Pickup Location */}
            <div className="form-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-green-500" />
                </div>
                <h2 className="form-section-title mb-0">Pickup Location</h2>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pickup_location">Address *</Label>
                  <Input
                    id="pickup_location"
                    placeholder="Street address or facility name"
                    value={formData.pickup_location}
                    onChange={(e) => handleChange('pickup_location', e.target.value)}
                    data-testid="pickup-address-input"
                  />
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pickup_city">City *</Label>
                    <Input
                      id="pickup_city"
                      placeholder="City"
                      value={formData.pickup_city}
                      onChange={(e) => handleChange('pickup_city', e.target.value)}
                      data-testid="pickup-city-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="pickup_state">State *</Label>
                    <Select value={formData.pickup_state} onValueChange={(v) => handleChange('pickup_state', v)}>
                      <SelectTrigger data-testid="pickup-state-select">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map(state => (
                          <SelectItem key={state.code} value={state.code}>{state.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="pickup_postcode">Postcode</Label>
                  <Input
                    id="pickup_postcode"
                    placeholder="e.g., 4305"
                    value={formData.pickup_postcode}
                    onChange={(e) => handleChange('pickup_postcode', e.target.value)}
                    maxLength={4}
                    className="w-32"
                    data-testid="pickup-postcode-input"
                  />
                  <p className="text-xs text-muted-foreground">Helps match nearby providers</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="pickup_date">Pickup Date *</Label>
                  <Input
                    id="pickup_date"
                    type="date"
                    value={formData.pickup_date}
                    onChange={(e) => handleChange('pickup_date', e.target.value)}
                    data-testid="pickup-date-input"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Location */}
            <div className="form-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="form-section-title mb-0">Delivery Location</h2>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="delivery_location">Address *</Label>
                  <Input
                    id="delivery_location"
                    placeholder="Street address or facility name"
                    value={formData.delivery_location}
                    onChange={(e) => handleChange('delivery_location', e.target.value)}
                    data-testid="delivery-address-input"
                  />
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="delivery_city">City *</Label>
                    <Input
                      id="delivery_city"
                      placeholder="City"
                      value={formData.delivery_city}
                      onChange={(e) => handleChange('delivery_city', e.target.value)}
                      data-testid="delivery-city-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="delivery_state">State *</Label>
                    <Select value={formData.delivery_state} onValueChange={(v) => handleChange('delivery_state', v)}>
                      <SelectTrigger data-testid="delivery-state-select">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map(state => (
                          <SelectItem key={state.code} value={state.code}>{state.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delivery_postcode">Postcode</Label>
                  <Input
                    id="delivery_postcode"
                    placeholder="e.g., 3000"
                    value={formData.delivery_postcode}
                    onChange={(e) => handleChange('delivery_postcode', e.target.value)}
                    maxLength={4}
                    className="w-32"
                    data-testid="delivery-postcode-input"
                  />
                  <p className="text-xs text-muted-foreground">Helps match nearby providers</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delivery_deadline">Delivery Deadline *</Label>
                  <Input
                    id="delivery_deadline"
                    type="date"
                    value={formData.delivery_deadline}
                    onChange={(e) => handleChange('delivery_deadline', e.target.value)}
                    data-testid="delivery-deadline-input"
                  />
                </div>

                {/* Authority to Leave Checkbox */}
                <div className="pt-4 border-t border-border">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.authority_to_leave}
                      onChange={(e) => handleChange('authority_to_leave', e.target.checked)}
                      className="mt-1 rounded border-border"
                      data-testid="authority-to-leave-checkbox"
                    />
                    <div>
                      <span className="font-medium text-sm">Authority to Leave Unattended</span>
                      <p className="text-xs text-muted-foreground mt-1">
                        I authorize the driver to leave the delivery in a safe place if no one is available to receive it.
                        The driver will take a photo as proof of delivery.
                      </p>
                    </div>
                  </label>
                  
                  {/* ATL Instructions - shown when checkbox is ticked */}
                  {formData.authority_to_leave && (
                    <div className="mt-4 ml-6">
                      <Label htmlFor="atl_instructions" className="text-sm">Where should the driver leave it?</Label>
                      <Textarea
                        id="atl_instructions"
                        value={formData.atl_instructions || ''}
                        onChange={(e) => handleChange('atl_instructions', e.target.value)}
                        placeholder="e.g., Behind the wheelie bin, Inside the back gate, On the front porch under cover..."
                        className="mt-2 h-20"
                        data-testid="atl-instructions-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Special Requirements */}
            <div className="form-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-yellow-500/10 rounded-sm flex items-center justify-center">
                  <Package className="w-5 h-5 text-yellow-500" />
                </div>
                <h2 className="form-section-title mb-0">Additional Info</h2>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="special_requirements">Special Requirements (Optional)</Label>
                <Textarea
                  id="special_requirements"
                  placeholder="Any special handling instructions, equipment needed, etc."
                  rows={3}
                  value={formData.special_requirements}
                  onChange={(e) => handleChange('special_requirements', e.target.value)}
                  data-testid="special-requirements-input"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="submit"
                className="btn-active flex-1"
                disabled={loading}
                data-testid="submit-job-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Posting Job...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Post Job & Notify Providers
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
